import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import { parseBidSnapshot, type BidSnapshot } from '@/api/bidSnapshotTypes';

const POLL_MS = 2000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollSnapshotUntilDone(profileId: string, snapshotId: string): Promise<BidSnapshot> {
  for (;;) {
    const res = await adsApi.getBidSnapshot(profileId, snapshotId);
    const snap = parseBidSnapshot(res.data);
    if (!snap) {
      throw new Error('Invalid snapshot response');
    }
    if (snap.status !== 'capturing') {
      return snap;
    }
    await sleep(POLL_MS);
  }
}

export type SaveBidSnapshotParams = {
  name: string;
  note?: string;
};

export function useBidSnapshotSave(profileId: string) {
  const queryClient = useQueryClient();
  const [savingSnapshot, setSavingSnapshot] = useState<BidSnapshot | null>(null);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    return () => {
      abortRef.current = true;
    };
  }, [profileId]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshots', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['bid-snapshot', profileId] });
  }, [profileId, queryClient]);

  const mutation = useMutation({
    mutationFn: async (params: SaveBidSnapshotParams) => {
      const createRes = await adsApi.createBidSnapshot(profileId, params);
      const created = parseBidSnapshot(createRes.data);
      if (!created) {
        throw new Error('Failed to start snapshot');
      }
      setSavingSnapshot(created);
      const final = await pollSnapshotUntilDone(profileId, created.id);
      if (abortRef.current) {
        return final;
      }
      setSavingSnapshot(final);
      return final;
    },
    onSettled: () => {
      invalidate();
    },
  });

  const isSaving = mutation.isPending || savingSnapshot?.status === 'capturing';

  return {
    save: mutation.mutateAsync,
    isSaving,
    savingSnapshot,
    reset: () => {
      setSavingSnapshot(null);
      mutation.reset();
    },
  };
}
